import type { IFilterableListItem, IMultiSelectProps, IOmit } from '@sixui/ui';
import { forwardRef } from 'react';
import { faCorner } from '@fortawesome/pro-light-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { Field, MultiSelect, Skeleton } from '@sixui/ui';
import stylex from '@stylexjs/stylex';

import type { IApiGetCategoryListItemResponseBody } from '~/lib/api/modules/Categories';
import type { ICategoryFilter } from '../CategorySelector/categoryFilterPredicate';
import { ErrorMessage } from '~/components/molecules/ErrorMessage';
import { makeTree } from '~/helpers/tree';
import { useCategoriesQuery } from '~/lib/api/modules/Categories/hooks/useCategoriesQuery';
import { categoryFilterPredicate } from '../CategorySelector/categoryFilterPredicate';

export type ICategoriesSelectorProps = IOmit<IMultiSelectProps, 'items'> & {
  appId: string;
  filter?: ICategoryFilter;
};

type ICategoryTreeLeaf = IApiGetCategoryListItemResponseBody & {
  depth: number;
  children?: Array<ICategoryTreeLeaf>;
};

const styles = stylex.create({
  treeLeafIcon: (depth: number) => ({
    marginLeft: `calc((16px + 0.5rem) * ${depth - 1})`,
    marginRight: '0.5rem',
    width: 16,
    height: 16,
    rotate: '180deg',
  }),
});

const renderOptionsTree = (
  tree: Array<ICategoryTreeLeaf>,
): Array<IFilterableListItem> =>
  tree
    .map(({ _id, name, depth, children }) => [
      {
        value: _id,
        label: name,
        icon:
          depth > 0 ? (
            <FontAwesomeIcon
              {...stylex.props(styles.treeLeafIcon(depth))}
              icon={faCorner}
            />
          ) : undefined,
      },
      ...(children && children?.length > 0 ? renderOptionsTree(children) : []),
    ])
    .flat();

export const CategoriesSelector = forwardRef<
  HTMLInputElement,
  ICategoriesSelectorProps
>(function CategoriesSelector(props, forwardedRef) {
  const { appId, filter, ...other } = props;
  const categoriesQuery = useCategoriesQuery({ appId });

  const tree = categoriesQuery.data?.categories
    ? makeTree(
        filter
          ? categoriesQuery.data.categories.filter((category) =>
              categoryFilterPredicate(category, filter),
            )
          : categoriesQuery.data.categories,
      )
    : undefined;

  const loaded = !!tree || !categoriesQuery.isLoading;
  const items = tree ? renderOptionsTree(tree) : [];

  return categoriesQuery.isError ? (
    <ErrorMessage error={categoriesQuery.error} />
  ) : loaded ? (
    <MultiSelect {...other} items={items} ref={forwardedRef} />
  ) : (
    <Skeleton>
      <Field />
    </Skeleton>
  );
});
